import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import { bindActionCreators } from 'redux'
import { ToastContainer } from 'react-toastify'

import { usePrevProp } from 'utils/hooks'
import Col from 'component/utils/Col'
import Grid from 'component/utils/Grid'
import ContentHeader from 'component/utils/ContentHeader'
import Notification from 'component/utils/Notification'
import screenType from 'component/menuMission/screenType'
import Pagination from 'component/pagination/Pagination'
import SuccessModal from 'component/Modal/SuccessModal'
import EditModal from 'component/justificationLinearConference/EditModal'
import Options from 'component/justificationLinearConference/Options'
import SideFilterForm from 'component/justificationLinearConference/SideFilterForm'
import Table from 'component/justificationLinearConference/Table'

import * as justificationsLinearConferenceActions from 'action/justificationsLinearConference'

import 'styles/overwrittenAndNew/newPageStyle.css'

const SAVE_API = 1
const UPDATE_API = 2

const DEFAULT_FILTER_INITIAL_VALUES = {
  inInactive: 2,
}

const DEFAULT_EDIT_INITIAL_VALUES = {
  params: [
    {
      dsJustification: '',
      inInactive: 0,
    },
  ],
}

const DEFAULT_PAGINATION = {
  hasPagination: true,
  page: 1,
  size: 10,
}

const JustificationLinearConference = ({
  auth: {
    user: { dsToken },
  },
  count,
  current,
  pages,
  getJustificationsLC,
  updateJustificationLC,
  removeJustificationLC,
  records = [],
  translate,
}) => {
  const [openEditModal, setOpenEditModal] = useState(false)
  const [successModalType, setSuccessModalType] = useState(null)

  const [editInitialValues, setEditInitialValues] = useState(
    DEFAULT_EDIT_INITIAL_VALUES
  )
  const [shouldSaveOrUpdate, setShouldSaveOrUpdate] = useState(SAVE_API)

  const [filters, setFilters] = useState(DEFAULT_FILTER_INITIAL_VALUES)
  const prevFilters = usePrevProp(filters)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
  const prevPagination = usePrevProp(pagination)
  const [sideFilterVisible, setSideFilterVisible] = useState(false)

  useEffect(() => {
    if (filters !== prevFilters) {
      if (pagination.page > 1) {
        setPagination({ ...pagination, page: 1 })
      } else {
        getJustificationsLC(dsToken, { ...filters, ...pagination })
      }
    }
  }, [filters])

  useEffect(() => {
    if (pagination !== prevPagination) {
      getJustificationsLC(dsToken, { ...filters, ...pagination })
    }
  }, [pagination])

  const toggleSideFilter = () => {
    setSideFilterVisible(!sideFilterVisible)
  }

  const deleteJustification = (value) => {
    removeJustificationLC(
      dsToken,
      value,
      setSuccessModalType(screenType.REMOVE_TYPE)
    )
  }

  const setUpdateModal = (record) => {
    setShouldSaveOrUpdate(UPDATE_API)

    const updateData = {
      params: [record],
      type: screenType.EDIT_PERSONAL_PROFILE_TYPE,
    }

    setEditInitialValues(updateData)
    setOpenEditModal(true)
  }

  const handleOpenEditModal = () => {
    setEditInitialValues({
      params: [
        {
          dsJustification: '',
          inInactive: 0,
        },
      ],
      type: screenType.CREATE_PERSONAL_PROFILE_TYPE,
    })
    setOpenEditModal(true)
    setShouldSaveOrUpdate(SAVE_API)
  }

  const submitSideFilterForm = (values) => {
    setSideFilterVisible(false)
    setFilters({ ...filters, ...values, o: Math.random })
  }

  const saveJustification = async (values) => {
    setOpenEditModal(false)

    if (shouldSaveOrUpdate === SAVE_API) {
      const callback = () => {
        setSuccessModalType(screenType.CREATE_PERSONAL_PROFILE_TYPE)
        setEditInitialValues({
          params: [
            {
              dsJustification: '',
              inInactive: 0,
            },
          ],
        })
      }

      await updateJustificationLC(dsToken, values, callback)
    } else {
      await updateJustificationLC(dsToken, values, () =>
        setSuccessModalType(screenType.EDIT_PERSONAL_PROFILE_TYPE)
      )
    }

    getJustificationsLC(dsToken, { ...filters, ...pagination })
  }

  const renderEditModal = () => {
    if (!openEditModal) return

    return (
      <EditModal
        closeModal={() => setOpenEditModal(false)}
        initialValues={editInitialValues}
        submit={saveJustification}
        token={dsToken}
        translate={translate}
      />
    )
  }

  const renderSuccessModal = () => {
    if (!successModalType) return

    return (
      <SuccessModal
        handleClose={() => setSuccessModalType(null)}
        msg={translate(
          `linear.conference.justification.modal.success.message.${successModalType}`
        )}
        size='md'
        t={translate}
      />
    )
  }

  const renderSideFilterForm = () => {
    if (!sideFilterVisible) return

    return (
      <SideFilterForm
        initialValues={filters}
        setSideFilterVisible={toggleSideFilter}
        submit={submitSideFilterForm}
        translate={translate}
      />
    )
  }

  const renderOptions = () => (
    <div className='new-style'>
      <Grid fluid className='new-upper-container'>
        <Col xs={12} md={6} className='float-right'>
          <Options
            setOpenEditModal={handleOpenEditModal}
            setSideFilterVisible={toggleSideFilter}
            translate={translate}
          />
        </Col>
      </Grid>
    </div>
  )

  const renderTable = () =>
    records.length ? (
      <Table
        handleDelete={deleteJustification}
        openEditModal={setUpdateModal}
        records={records}
        toggleInInactive={(values) => updateJustificationLC(dsToken, values)}
        translate={translate}
      />
    ) : (
      <Col
        xs={12}
        className='margin-top margin-right'
        style={{ marginLeft: `${sideFilterVisible ? 0.5 : 1}rem` }}
      >
        <Notification
          message={translate('no.data.found')}
          className='notification-warning'
        />
      </Col>
    )

  const renderPagination = () =>
    records.length && pages > 1 ? (
      <Col className='align-center margin-bottom margin-top-triple new-style-pagination'>
        <Pagination
          count={count}
          current={current}
          getPage={(page) => setPagination({ ...pagination, page })}
          pages={pages}
        />
      </Col>
    ) : (
      ''
    )

  return (
    <>
      <div className='new-style'>
        <ContentHeader>
          {translate('linear.conference.justification.title.screen')}
        </ContentHeader>
        <div className='padding-bottom new-style-filter'>{renderOptions()}</div>
        <div className='new-style-table'>
          <Grid fluid className='no-padding'>
            <div className='outer-table new-outer-table'>
              <div>{renderTable()}</div>
              {renderPagination()}
            </div>
          </Grid>
        </div>
        {renderEditModal()}
        {renderSuccessModal()}
        <ToastContainer newestOnTop />
      </div>
      {renderSideFilterForm()}
    </>
  )
}

/* istanbul ignore next */
const mapStateToProps = ({
  paginateReducer,
  justificationsLinearConferenceReducer,
}) => ({
  count: paginateReducer.count,
  current: paginateReducer.current,
  pages: paginateReducer.pages,
  records: justificationsLinearConferenceReducer.list.records,
  isFetching: justificationsLinearConferenceReducer.list.isFetching,
  error: justificationsLinearConferenceReducer.edit.error,
})

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(justificationsLinearConferenceActions, dispatch)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  translate(null, {
    wait: true,
    translateFuncName: 'translate',
  })(JustificationLinearConference)
)

JustificationLinearConference.propTypes = {
  auth: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  getJustificationsLC: PropTypes.func.isRequired,
  updateJustificationLC: PropTypes.func.isRequired,
  removeJustificationLC: PropTypes.func.isRequired,
  records: PropTypes.array.isRequired,
  translate: PropTypes.func.isRequired,
}

export { JustificationLinearConference }
